import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, subirArchivo } from "../lib/api";
import { fecha, fechaHora } from "../lib/formato";
import { Aviso, Cabecera, Campo, Cargando, Etiqueta, Modal, Panel, TarjetaDato, Vacio } from "../componentes/piezas";
import { useSesion } from "../lib/sesion";
import { useIdioma } from "../lib/idioma";

interface Publicacion {
  id: string;
  titulo: string;
  cuerpo: string;
  imagenes: string;
  publicada: boolean;
  fechaPublicacion: string;
  usuario: { nombre: string } | null;
}

const fotosDe = (json: string): string[] => {
  try {
    const v = JSON.parse(json || "[]");
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
};

interface Solicitud {
  id: string;
  nombre: string;
  apellidos: string;
  documentoTipo: string;
  documentoNumero: string;
  fechaNacimiento: string;
  telefono: string | null;
  email: string | null;
  ciudad: string | null;
  avalistaNumero: number | null;
  mensaje: string | null;
  estado: string;
  motivoRechazo: string | null;
  createdAt: string;
  socio: { id: string; numeroSocio: number } | null;
}

/** Noticias que el club pública en el portal y solicitudes de alta que llegan de fuera. */
export default function Comunidad() {
  const { t } = useIdioma();
  const { puede, club } = useSesion();
  const clienteConsultas = useQueryClient();
  const [pestana, setPestana] = useState<"solicitudes" | "noticias">("solicitudes");
  const [editando, setEditando] = useState<Publicacion | "nueva" | null>(null);
  const [rechazando, setRechazando] = useState<Solicitud | null>(null);
  const [error, setError] = useState("");
  const [mensaje, setMensaje] = useState("");

  const solicitudes = useQuery({ queryKey: ["solicitudes"], queryFn: () => api<Solicitud[]>("/comunidad/solicitudes") });
  const publicaciones = useQuery({ queryKey: ["publicaciones"], queryFn: () => api<Publicacion[]>("/comunidad/publicaciones") });

  const refrescar = () => {
    void clienteConsultas.invalidateQueries({ queryKey: ["solicitudes"] });
    void clienteConsultas.invalidateQueries({ queryKey: ["publicaciones"] });
    void clienteConsultas.invalidateQueries({ queryKey: ["panel"] });
  };

  const pendientes = solicitudes.data?.filter((s) => s.estado === "PENDIENTE") ?? [];
  const resueltas = solicitudes.data?.filter((s) => s.estado !== "PENDIENTE") ?? [];
  const enlaceAlta = `${window.location.origin}/portal/${club?.slug ?? ""}/alta`;

  async function aprobar(s: Solicitud) {
    setError("");
    try {
      const socio = await api<{ numeroSocio: number }>(`/comunidad/solicitudes/${s.id}/aprobar`, { metodo: "POST" });
      setMensaje(
        t("cm.aprobada", { persona: `${s.nombre} ${s.apellidos}`, numero: socio.numeroSocio }),
      );
      refrescar();
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : t("cm.falloAprobar"));
    }
  }

  return (
    <>
      <Cabecera titulo={t("cm.titulo")} descripcion={t("cm.descripcion")}>
        {pestana === "noticias" && puede("gestionarPublicaciones") && (
          <button className="boton" onClick={() => setEditando("nueva")}>
            {t("cm.nuevaNoticia")}
          </button>
        )}
      </Cabecera>

      <Aviso tipo="error">{error}</Aviso>
      <Aviso tipo="ok">{mensaje}</Aviso>

      <div className="fila" style={{ gap: 8, marginBottom: 16 }}>
        <button className={`boton ${pestana === "solicitudes" ? "" : "secundario"}`} onClick={() => setPestana("solicitudes")}>
          {t("cm.tabSolicitudes")} {pendientes.length > 0 && `(${pendientes.length})`}
        </button>
        <button className={`boton ${pestana === "noticias" ? "" : "secundario"}`} onClick={() => setPestana("noticias")}>
          {t("cm.tabNoticias")}
        </button>
      </div>

      {pestana === "solicitudes" ? (
        <>
          <div className="rejilla tres" style={{ marginBottom: 16 }}>
            <TarjetaDato
              etiqueta={t("cm.pendientes")}
              valor={pendientes.length}
              color={pendientes.length > 0 ? "var(--ambar)" : undefined}
            />
            <TarjetaDato etiqueta={t("cm.resueltas")} valor={resueltas.length} />
            <div className="tarjeta-dato">
              <div className="rotulo">{t("cm.enlaceAlta")}</div>
              <p className="pequeno" style={{ margin: "6px 0 8px", wordBreak: "break-all" }}>
                {enlaceAlta}
              </p>
              <button className="boton secundario" onClick={() => void navigator.clipboard?.writeText(enlaceAlta)}>
                {t("cm.copiarEnlace")}
              </button>
            </div>
          </div>

          <Panel titulo={t("cm.solicitudesPendientes")} sinCuerpo>
            {solicitudes.isLoading ? (
              <Cargando />
            ) : pendientes.length === 0 ? (
              <Vacio>{t("cm.sinPendientes")}</Vacio>
            ) : (
              <div className="tabla-envoltorio">
                <table className="tabla">
                  <thead>
                    <tr>
                      <th>{t("cm.recibida")}</th>
                      <th>{t("cm.persona")}</th>
                      <th>{t("cm.contacto")}</th>
                      <th>{t("cm.avalista")}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {pendientes.map((s) => (
                      <tr key={s.id}>
                        <td className="pequeno suave">{fechaHora(s.createdAt)}</td>
                        <td>
                          <strong>
                            {s.nombre} {s.apellidos}
                          </strong>
                          <div className="suave pequeno">
                            {t("cm.nacidoEl", {
                              documento: `${s.documentoTipo} ${s.documentoNumero}`,
                              fecha: fecha(s.fechaNacimiento),
                            })}
                          </div>
                          {s.mensaje && <div className="pequeno">«{s.mensaje}»</div>}
                        </td>
                        <td className="pequeno">
                          {s.telefono ?? "—"}
                          {s.email && <div className="suave">{s.email}</div>}
                          {s.ciudad && <div className="suave">{s.ciudad}</div>}
                        </td>
                        <td className="pequeno tabular">
                          {s.avalistaNumero ? t("cm.socioNumero", { numero: s.avalistaNumero }) : "—"}
                        </td>
                        <td>
                          <div className="fila" style={{ gap: 2, justifyContent: "flex-end" }}>
                            <button className="boton" onClick={() => void aprobar(s)}>
                              {t("cm.aprobar")}
                            </button>
                            <button className="boton fantasma" onClick={() => setRechazando(s)}>
                              {t("cm.rechazar")}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>

          {resueltas.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <Panel titulo={t("cm.resueltas")} sinCuerpo>
                <div className="tabla-envoltorio">
                  <table className="tabla">
                    <thead>
                      <tr>
                        <th>{t("cm.persona")}</th>
                        <th>{t("cm.estado")}</th>
                        <th>{t("cm.resultado")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resueltas.map((s) => (
                        <tr key={s.id}>
                          <td className="pequeno">
                            {s.nombre} {s.apellidos}
                          </td>
                          <td>
                            {s.estado === "APROBADA" ? (
                              <Etiqueta tipo="verde">{t("cm.etiquetaAprobada")}</Etiqueta>
                            ) : (
                              <Etiqueta tipo="roja">{t("cm.etiquetaRechazada")}</Etiqueta>
                            )}
                          </td>
                          <td className="pequeno suave">
                            {s.socio
                              ? t("cm.socioNumero", { numero: s.socio.numeroSocio })
                              : (s.motivoRechazo ?? "—")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
            </div>
          )}
        </>
      ) : (
        <Panel sinCuerpo>
          {publicaciones.isLoading ? (
            <Cargando />
          ) : !publicaciones.data || publicaciones.data.length === 0 ? (
            <Vacio>{t("cm.sinNoticias")}</Vacio>
          ) : (
            <div className="tabla-envoltorio">
              <table className="tabla">
                <thead>
                  <tr>
                    <th>{t("cm.fecha")}</th>
                    <th>{t("cm.tituloColumna")}</th>
                    <th>{t("cm.estado")}</th>
                    <th>{t("cm.publicadoPor")}</th>
                    {puede("gestionarPublicaciones") && <th />}
                  </tr>
                </thead>
                <tbody>
                  {publicaciones.data.map((p) => (
                    <tr key={p.id} style={p.publicada ? undefined : { opacity: 0.55 }}>
                      <td className="pequeno">{fecha(p.fechaPublicacion)}</td>
                      <td>
                        <strong>{p.titulo}</strong>
                        <div className="suave pequeno">{p.cuerpo.slice(0, 90)}…</div>
                        {fotosDe(p.imagenes).length > 0 && (
                          <div className="fila" style={{ gap: 4, marginTop: 4 }}>
                            {fotosDe(p.imagenes).slice(0, 4).map((foto) => (
                              <img key={foto} src={foto} alt="" className="miniatura" />
                            ))}
                            {fotosDe(p.imagenes).length > 4 && (
                              <span className="suave pequeno">+{fotosDe(p.imagenes).length - 4}</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td>
                        {p.publicada ? (
                          <Etiqueta tipo="verde">{t("cm.publicada")}</Etiqueta>
                        ) : (
                          <Etiqueta>{t("cm.borrador")}</Etiqueta>
                        )}
                      </td>
                      <td className="pequeno suave">{p.usuario?.nombre ?? "—"}</td>
                      {puede("gestionarPublicaciones") && (
                        <td>
                          <div className="fila" style={{ gap: 2, justifyContent: "flex-end" }}>
                            <button className="boton fantasma" onClick={() => setEditando(p)}>
                              {t("cm.editar")}
                            </button>
                            <button
                              className="boton fantasma"
                              onClick={async () => {
                                if (!window.confirm(t("cm.confirmarBorrar", { titulo: p.titulo }))) return;
                                await api(`/comunidad/publicaciones/${p.id}`, { metodo: "DELETE" });
                                refrescar();
                              }}
                            >
                              {t("cm.borrar")}
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      )}

      {editando && (
        <DialogoNoticia
          publicacion={editando === "nueva" ? null : editando}
          alCerrar={() => setEditando(null)}
          alFallar={setError}
          alHecho={() => {
            setEditando(null);
            refrescar();
          }}
        />
      )}

      {rechazando && (
        <DialogoRechazo
          solicitud={rechazando}
          alCerrar={() => setRechazando(null)}
          alFallar={setError}
          alHecho={() => {
            setRechazando(null);
            refrescar();
          }}
        />
      )}
    </>
  );
}

function DialogoNoticia({
  publicacion,
  alCerrar,
  alHecho,
  alFallar,
}: {
  publicacion: Publicacion | null;
  alCerrar: () => void;
  alHecho: () => void;
  alFallar: (mensaje: string) => void;
}) {
  const { t } = useIdioma();
  const [titulo, setTitulo] = useState(publicacion?.titulo ?? "");
  const [cuerpo, setCuerpo] = useState(publicacion?.cuerpo ?? "");
  const [publicada, setPublicada] = useState(publicacion?.publicada ?? true);
  const [enviando, setEnviando] = useState(false);

  // Las fotos se suben contra una noticia ya creada, así que primero hay que guardarla
  const [id, setId] = useState(publicacion?.id ?? null);
  const [fotos, setFotos] = useState<string[]>(fotosDe(publicacion?.imagenes ?? "[]"));
  const [subiendo, setSubiendo] = useState(false);
  const [fallo, setFallo] = useState("");
  const [avisar, setAvisar] = useState(false);

  async function guardarNoticia(opciones: { avisar?: boolean } = {}): Promise<string> {
    const datos = { titulo, cuerpo, publicada };
    if (id) {
      await api(`/comunidad/publicaciones/${id}`, { metodo: "PATCH", cuerpo: datos });
      if (opciones.avisar && publicada) await api(`/comunidad/publicaciones/${id}/avisar`, { metodo: "POST" });
      return id;
    }
    const creada = await api<{ id: string }>("/comunidad/publicaciones", {
      metodo: "POST",
      cuerpo: { ...datos, avisar: opciones.avisar ?? false },
    });
    setId(creada.id);
    return creada.id;
  }

  async function anadirFoto(archivo: File) {
    setSubiendo(true);
    setFallo("");
    try {
      const idNoticia = await guardarNoticia();
      const r = await subirArchivo<{ imagenes: string[] }>(`/comunidad/publicaciones/${idNoticia}/imagenes`, archivo);
      setFotos(r.imagenes);
    } catch (error) {
      setFallo(error instanceof Error ? error.message : t("cm.n.falloSubir"));
    } finally {
      setSubiendo(false);
    }
  }

  async function quitarFoto(url: string) {
    if (!id) return;
    try {
      const r = await api<{ imagenes: string[] }>(`/comunidad/publicaciones/${id}/imagenes`, {
        metodo: "DELETE",
        cuerpo: { url },
      });
      setFotos(r.imagenes);
    } catch (error) {
      setFallo(error instanceof Error ? error.message : t("cm.n.falloQuitar"));
    }
  }

  return (
    <Modal
      titulo={publicacion ? t("cm.n.editar") : t("cm.n.nueva")}
      alCerrar={alCerrar}
      acciones={
        <>
          <button className="boton secundario" onClick={alCerrar}>
            Cancelar
          </button>
          <button
            className="boton"
            disabled={enviando || !titulo.trim() || !cuerpo.trim()}
            onClick={async () => {
              setEnviando(true);
              try {
                await guardarNoticia({ avisar });
                alHecho();
              } catch (error) {
                alFallar(error instanceof Error ? error.message : t("cm.n.fallo"));
                alCerrar();
              } finally {
                setEnviando(false);
              }
            }}
          >
            {t("g.guardar")}
          </button>
        </>
      }
    >
      <Campo etiqueta={t("cm.n.titulo")}>
        <input value={titulo} onChange={(e) => setTitulo(e.target.value)} autoFocus />
      </Campo>
      <Campo etiqueta={t("cm.n.texto")} ayuda={t("cm.n.textoAyuda")}>
        <textarea rows={6} value={cuerpo} onChange={(e) => setCuerpo(e.target.value)} />
      </Campo>
      <label className="casilla">
        <input type="checkbox" checked={publicada} onChange={(e) => setPublicada(e.target.checked)} />
        <span>{t("cm.n.publicada")}</span>
      </label>

      <label className="casilla">
        <input
          type="checkbox"
          checked={avisar}
          disabled={!publicada}
          onChange={(e) => setAvisar(e.target.checked)}
        />
        <span>
          {t("cm.n.avisar")}
          <br />
          <span className="ayuda">{t("cm.n.avisarAyuda")}</span>
        </span>
      </label>

      <div className="rotulo" style={{ marginBottom: 6 }}>
        {t("cm.n.fotos")}
      </div>
      <Aviso tipo="error">{fallo}</Aviso>

      {fotos.length > 0 && (
        <div className="galeria" style={{ marginBottom: 10 }}>
          {fotos.map((foto) => (
            <div key={foto} className="galeria-foto editable">
              <img src={foto} alt="" />
              <button
                type="button"
                className="quitar"
                title={t("cm.n.quitar")}
                onClick={() => void quitarFoto(foto)}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      <label className="boton secundario" style={{ cursor: subiendo ? "wait" : "pointer" }}>
        {subiendo ? t("cm.n.subiendo") : fotos.length > 0 ? t("cm.n.otraFoto") : t("cm.n.anadirFotos")}
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          hidden
          disabled={subiendo || !titulo.trim() || !cuerpo.trim()}
          onChange={(e) => {
            const archivo = e.target.files?.[0];
            e.target.value = "";
            if (archivo) void anadirFoto(archivo);
          }}
        />
      </label>
      <p className="ayuda" style={{ marginBottom: 0 }}>
        {!titulo.trim() || !cuerpo.trim() ? t("cm.n.escribePrimero") : t("cm.n.hastaOcho")}
      </p>
    </Modal>
  );
}

function DialogoRechazo({
  solicitud,
  alCerrar,
  alHecho,
  alFallar,
}: {
  solicitud: Solicitud;
  alCerrar: () => void;
  alHecho: () => void;
  alFallar: (mensaje: string) => void;
}) {
  const { t } = useIdioma();
  const [motivo, setMotivo] = useState("");
  const [enviando, setEnviando] = useState(false);

  return (
    <Modal
      titulo={t("cm.r.titulo", { nombre: solicitud.nombre })}
      alCerrar={alCerrar}
      acciones={
        <>
          <button className="boton secundario" onClick={alCerrar}>
            Cancelar
          </button>
          <button
            className="boton peligro"
            disabled={enviando}
            onClick={async () => {
              setEnviando(true);
              try {
                await api(`/comunidad/solicitudes/${solicitud.id}/rechazar`, { metodo: "POST", cuerpo: { motivo } });
                alHecho();
              } catch (fallo) {
                alFallar(fallo instanceof Error ? fallo.message : t("cm.r.fallo"));
                alCerrar();
              } finally {
                setEnviando(false);
              }
            }}
          >
            {t("cm.r.rechazar")}
          </button>
        </>
      }
    >
      <p className="suave" style={{ marginTop: 0 }}>
        {t("cm.r.queda")}
      </p>
      <Campo etiqueta={t("cm.r.motivo")}>
        <textarea rows={3} value={motivo} onChange={(e) => setMotivo(e.target.value)} autoFocus />
      </Campo>
    </Modal>
  );
}
